<template>
  <div class="container mt-5">
    <div class="card modern-card">
      <div class="card-header">
        <h4 class="mb-0">
          <i class="fas fa-mobile-alt me-2"></i>
          Paiement Mobile Money / Carte
        </h4>
      </div>

      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-3 text-muted">Préparation du paiement...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          <i class="fas fa-exclamation-circle me-2"></i>
          {{ error }}
        </div>

        <div v-else>
          <div class="payment-info mb-4">
            <h5 class="info-title">Détails du paiement</h5>
            <div class="info-content">
              <div class="info-row">
                <span class="info-label">Référence BESC</span>
                <span class="info-value">{{ bescRef }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Montant</span>
                <span class="info-value">{{ formatNumber(amount) }} {{ currency }}</span>
              </div>
            </div>
          </div>

          <div class="alert alert-info-custom mb-4">
            <div class="alert-header">
              <i class="fas fa-info-circle me-2"></i>
              <span>Moyens de paiement acceptés</span>
            </div>
            <ul class="payment-methods">
              <li><i class="fas fa-check-circle me-2"></i>Mobile Money (MTN, Moov, etc.)</li>
              <li><i class="fas fa-check-circle me-2"></i>Cartes Visa / Mastercard</li>
            </ul>
          </div>

          <button class="btn btn-primary btn-payment w-100" @click="openKKiaPay" :disabled="isProcessing">
            <i v-if="isProcessing" class="fa fa-spinner fa-spin me-2"></i>
            <i v-else class="fas fa-wallet me-2"></i>
            {{ isProcessing ? 'Traitement en cours...' : 'Payer maintenant' }}
          </button>

          <div class="mt-3 text-center">
            <router-link to="/liste-besc" class="btn btn-outline-secondary btn-cancel">
              <i class="fas fa-arrow-left me-2"></i>
              Annuler et retourner
            </router-link>
          </div>

          <div class="text-center mt-4">
            <small class="secure-badge">
              <i class="fas fa-lock me-1"></i>
              Paiement sécurisé par KKiaPay
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError, success as showSuccess } from '@/utils/utils';

declare global {
  interface Window {
    openKkiapayWidget: any;
    addSuccessListener: any;
    addFailedListener: any;
  }
}

export default defineComponent({
  name: 'PaiementKKiaPay',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const bescId = ref(route.params.id as string);
    const bescRef = ref('');
    const amount = ref(0);
    const currency = ref('XOF');
    const loading = ref(true);
    const error = ref('');
    const isProcessing = ref(false);
    const publicKey = ref('');

    const formatNumber = (val: number) => {
      return new Intl.NumberFormat('fr-FR').format(val);
    };

    const loadPaymentData = async () => {
      try {
        console.log(' Chargement des données de paiement...');
        const { data } = await ApiService.post(
          `/besc/${bescId.value}/payment/kkiapay/init`,
          {}
        );

        bescRef.value = data.data.bescRef;
        amount.value = data.data.amount;
        currency.value = data.data.currency || 'XOF'; 
        publicKey.value = data.data.publicKey;

        console.log(' Données chargées:', {
          bescRef: bescRef.value,
          amount: amount.value,
          currency: currency.value
        });
        loading.value = false;
      } catch (err: any) {
        console.error(' Erreur chargement:', err);
        error.value = err.response?.data?.message || 'Erreur lors du chargement';
        loading.value = false;
      }
    };

    const loadKKiaPayScript = () => {
      return new Promise<void>((resolve, reject) => {
        if (document.getElementById('kkiapay-script')) {
          console.log(' Script KKiaPay déjà chargé');
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.id = 'kkiapay-script';
        script.src = 'https://cdn.kkiapay.me/k.js';
        script.async = true;

        script.onload = () => {
          console.log(' Script KKiaPay chargé avec succès');
          resolve();
        };

        script.onerror = () => {
          console.error(' Impossible de charger le script KKiaPay');
          error.value = 'Impossible de charger KKiaPay';
          reject(new Error('Script loading failed'));
        };

        document.head.appendChild(script);
      });
    };

    // Fonction de vérification avec retry
    const verifyPaymentWithRetry = async (transactionId: string, maxRetries = 3) => {
      console.log(` Tentative de vérification (${maxRetries} essais restants)...`);

      for (let i = 0; i < maxRetries; i++) {
        try {
          // Attendre un peu avant chaque tentative (sauf la première)
          if (i > 0) {
            const delay = 2000 * i; // 2s, 4s, 6s
            console.log(` Attente de ${delay}ms avant nouvelle tentative...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }

          console.log(` Vérification tentative ${i + 1}/${maxRetries}`);

          const { data } = await ApiService.post(
            `/besc/${bescId.value}/payment/kkiapay/verify`,
            { transactionId }
          );

          console.log(' Vérification réussie:', data);
          return data;
        } catch (err: any) {
          console.error(` Tentative ${i + 1} échouée:`, err.response?.data?.message || err.message);

          // Si c'est la dernière tentative, on lance l'erreur
          if (i === maxRetries - 1) {
            throw err;
          }
        }
      }
    };

    // Dans handleSuccess, AJOUTER un log avant redirection :

    const handleSuccess = async (response: any) => {
      console.log(' Paiement réussi côté KKiaPay:', response);
      console.log(' Transaction ID:', response.transactionId);

      isProcessing.value = true;

      try {
        // Vérifier le paiement
        console.log(' Vérification du paiement...');
        await verifyPaymentWithRetry(response.transactionId);
        console.log(' Paiement vérifié côté backend');

        showSuccess(' Paiement effectué avec succès !');

        try {
          console.log(' Soumission à validation...');
          await ApiService.post(`/besc/${bescId.value}/submit-for-validation`, {
            comment: `Paiement confirmé via KKiaPay - Transaction ID: ${response.transactionId}`
          });
          console.log(' Dossier soumis à validation avec succès');
          showSuccess(' Dossier soumis à validation !');
        } catch (validationErr: any) {
          console.error(' Erreur soumission validation:', validationErr);
          // On continue quand même
        }

        // Redirection AVEC rechargement forcé
        console.log(' Redirection vers page de succès...');
        setTimeout(() => {
          window.location.href = `/paiement-success?bescId=${bescId.value}&method=kkiapay&transactionId=${response.transactionId}&reload=true`;
        }, 2000); // Augmenter à 2s pour laisser le temps au backend

      } catch (err: any) {
        console.error(' Erreur vérification finale:', err);
        showError(
          err.response?.data?.message ||
          'Erreur lors de la vérification du paiement. ID transaction: ' + response.transactionId
        );
        isProcessing.value = false;
      }
    };

    const handleFailed = (errorData: any) => {
      console.error(' Paiement échoué:', errorData);
      showError('Le paiement a échoué. Veuillez réessayer.');
      isProcessing.value = false;
    };

    const openKKiaPay = async () => {
      if (!publicKey.value) {
        console.error(' Clé publique KKiaPay manquante');
        showError('Configuration KKiaPay manquante');
        return;
      }

      console.log(' Ouverture du widget KKiaPay...');
      console.log(' Paramètres:', {
        amount: amount.value,
        bescRef: bescRef.value,
        publicKey: publicKey.value.substring(0, 10) + '...'
      });

      isProcessing.value = true;

      try {
        await loadKKiaPayScript();

        if (!window.openKkiapayWidget) {
          throw new Error('Widget KKiaPay non disponible');
        }

        // Enregistrer les listeners
        if (window.addSuccessListener) {
          window.addSuccessListener(handleSuccess);
          console.log(' Listener de succès enregistré');
        }

        if (window.addFailedListener) {
          window.addFailedListener(handleFailed);
          console.log(' Listener d\'échec enregistré');
        }

        // Ouvrir le widget avec la bonne clé publique
        window.openKkiapayWidget({
          amount: amount.value,
          position: "center",
          callback: "",
          data: bescRef.value,
          theme: "#0a59a4",
          key: process.env.VUE_APP_KKIAPAY_PUBLIC_KEY || publicKey.value,
          sandbox: true, // Ou false en production
        });

        console.log(' Widget KKiaPay ouvert');
        isProcessing.value = false;

      } catch (err: any) {
        console.error(' Erreur KKiaPay:', err);
        showError('Erreur lors de l\'ouverture du widget de paiement');
        isProcessing.value = false;
      }
    };

    onMounted(async () => {
      console.log(' Composant KKiaPay monté');
      console.log(' BESC ID:', bescId.value);
      await loadPaymentData();
    });

    onBeforeUnmount(() => {
      console.log(' Nettoyage du composant KKiaPay');
    });

    return {
      bescRef,
      amount,
      loading,
      error,
      isProcessing,
      formatNumber,
      openKKiaPay
    };
  }
});
</script>

<style scoped>
/* Styles identiques */
.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #0a59a4 0%, #0d6ec9 100%);
  color: white !important;
  padding: 1.5rem;
  border: none;
}

.card-header h4 {
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
  color: white !important;
}

.card-header h4 i {
  color: white !important;
}

.card-body {
  padding: 2rem;
}

.payment-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #0a59a4;
}

.info-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.info-label {
  color: #6c757d;
  font-size: 0.95rem;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

.alert-info-custom {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 1.25rem;
}

.alert-header {
  color: #0a59a4;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.payment-methods {
  list-style: none;
  padding: 0;
  margin: 0;
}

.payment-methods li {
  color: #495057;
  padding: 0.5rem 0;
  font-size: 0.95rem;
}

.payment-methods i {
  color: #0a59a4;
}

.btn-payment {
  background-color: #0a59a4;
  border-color: #0a59a4;
  color: white;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-payment:hover:not(:disabled) {
  background-color: #084a8a;
  border-color: #084a8a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 89, 164, 0.3);
}

.btn-payment:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
}

.btn-cancel {
  border-radius: 8px;
  padding: 0.625rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.secure-badge {
  display: inline-flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.secure-badge i {
  color: #28a745;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-width: 0.25em;
}

.text-primary {
  color: #0a59a4 !important;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c2c7;
  color: #842029;
  border-radius: 8px;
  padding: 1rem;
}

@media (max-width: 576px) {
  .card-body {
    padding: 1.5rem;
  }

  .payment-info {
    padding: 1rem;
  }

  .btn-payment {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
  }
}
</style>